import { createSlice } from '@reduxjs/toolkit';
import { PayloadAction } from '@reduxjs/toolkit';

import { DEFAULT_QUOTE_VALIDITY } from '../../interfaces/RFXBusinessModel';
import { RFXRequestForm } from '../../interfaces/RFXRequestForm';
import { RootState } from '../../store';

export const EMPTY_STRING: string = '';
export const START_OF_DATE_TIME: string = '00:00';
export const END_OF_DATE_TIME: string = '23:59';

export interface RFXInformationProps {
  currentBusinessUnit: string;
  currentRFXName: string;
  currentSiteNameFBN: string;
  currentBeginDate: string;
  currentBeginTime: string;
  currentDueDate: string;
  currentDueTime: string;
  currentNeedByDate: string;
  currentNeedByTime: string;
  currentRegion: string;
  currentRegions: string[];
  currentCategory: string;
  currentSubCategory: string;
  currentCategories: string[];
  currentCurrency: string;
  currentOrganization: string;
  currentDepartment: string;
  currentProject: string;
  currentOverview: string;
  currentProcessDetails: string;
  currentQuoteTemplateType: string;
  currentRFXLineItemClassId: string;
  currentQuoteValidity: number;
}

const initialState: RFXInformationProps = {
  currentBusinessUnit: EMPTY_STRING,
  currentRFXName: EMPTY_STRING,
  currentSiteNameFBN: EMPTY_STRING,
  currentBeginDate: EMPTY_STRING,
  currentBeginTime: START_OF_DATE_TIME,
  currentDueDate: EMPTY_STRING,
  currentDueTime: END_OF_DATE_TIME,
  currentNeedByDate: EMPTY_STRING,
  currentNeedByTime: END_OF_DATE_TIME,
  currentRegion: EMPTY_STRING,
  currentRegions: [],
  currentCategory: EMPTY_STRING,
  currentSubCategory: EMPTY_STRING,
  currentCategories: [],
  currentCurrency: EMPTY_STRING,
  currentOrganization: EMPTY_STRING,
  currentDepartment: EMPTY_STRING,
  currentProject: EMPTY_STRING,
  currentOverview: EMPTY_STRING,
  currentProcessDetails: EMPTY_STRING,
  currentQuoteTemplateType: EMPTY_STRING,
  currentRFXLineItemClassId: EMPTY_STRING,
  currentQuoteValidity: DEFAULT_QUOTE_VALIDITY
}

const rfxInformationSlice = createSlice({
  name: "rfxInformation",
  initialState,
  reducers: {
    setCurrentBusinessUnit(state, action: PayloadAction<string>) {
      state.currentBusinessUnit = action.payload;
    },
    setCurrentRFXName(state, action: PayloadAction<string>) {
      state.currentRFXName = action.payload;
    },
    setCurrentSiteNameFBN(state, action: PayloadAction<string>) {
      state.currentSiteNameFBN = action.payload;
    },
    setCurrentBeginDate(state, action: PayloadAction<string>) {
      state.currentBeginDate = action.payload;
    },
    setCurrentBeginTime(state, action: PayloadAction<string>) {
      state.currentBeginTime = action.payload;
    },
    setCurrentDueDate(state, action: PayloadAction<string>) {
      state.currentDueDate = action.payload;
    },
    setCurrentDueTime(state, action: PayloadAction<string>) {
      state.currentDueTime = action.payload;
    },
    setCurrentNeedByDate(state, action: PayloadAction<string>) {
      state.currentNeedByDate = action.payload;
    },
    setCurrentNeedByTime(state, action: PayloadAction<string>) {
      state.currentNeedByTime = action.payload;
    },
    setCurrentRegion(state, action: PayloadAction<string>) {
      state.currentRegion = action.payload;
    },
    setCurrentRegions(state, action: PayloadAction<string[]>) {
      state.currentRegions = action.payload;
    },
    setCurrentCategory(state, action: PayloadAction<string>) {
      state.currentCategory = action.payload;
    },
    setCurrentSubCategory(state, action: PayloadAction<string>) {
      state.currentSubCategory = action.payload;
    },
    setCurrentCategories(state, action: PayloadAction<string[]>) {
      state.currentCategories = action.payload;
    },
    setCurrentCurrency(state, action: PayloadAction<string>) {
      state.currentCurrency = action.payload;
    },
    setCurrentOrganization(state, action: PayloadAction<string>) {
      state.currentOrganization = action.payload;
    },
    setCurrentDepartment(state, action: PayloadAction<string>) {
      state.currentDepartment = action.payload;
    },
    setCurrentProject(state, action: PayloadAction<string>) {
      state.currentProject = action.payload;
    },
    setCurrentOverview(state, action: PayloadAction<string>) {
      state.currentOverview = action.payload;
    },
    setCurrentProcessDetails(state, action: PayloadAction<string>) {
      state.currentProcessDetails = action.payload;
    },
    setCurrentQuoteTemplateType(state, action: PayloadAction<string>) {
      state.currentQuoteTemplateType = action.payload;
    },
    setCurrentRFXLineItemClassId(state, action: PayloadAction<string>) {
      state.currentRFXLineItemClassId = action.payload;
    },
    setCurrentQuoteValidity(state, action: PayloadAction<number>) {
      state.currentQuoteValidity = action.payload;
    },
    setCurrentRFXInformation(state, action: PayloadAction<RFXRequestForm>) {
      state.currentRFXName = action.payload.generalInformation.rfxName;
      state.currentBeginDate = action.payload.generalInformation.beginDate;
      state.currentBeginTime = action.payload.generalInformation.beginTime;
      state.currentDueDate = action.payload.generalInformation.dueDate;
      state.currentDueTime = action.payload.generalInformation.dueTime;
      state.currentNeedByDate = action.payload.generalInformation.needByDate;
      state.currentNeedByTime = action.payload.generalInformation.needByTime;
      state.currentRegion = action.payload.generalInformation.region.label!;
      state.currentRegions = action.payload.generalInformation.regions ?
        action.payload.generalInformation.regions.map(region => region.label!) : [];
      state.currentCategory = action.payload.generalInformation.category?.label ?? EMPTY_STRING;
      state.currentSubCategory = action.payload.generalInformation.subCategory?.label ?? EMPTY_STRING;
      state.currentCategories = action.payload.generalInformation.categories ?
        action.payload.generalInformation.categories.map(category => category.label!) : [];
      state.currentCurrency = action.payload.generalInformation.currency.label!;
      state.currentOrganization = action.payload.generalInformation.organization.label!;
      state.currentDepartment = action.payload.generalInformation.department.label!;
      state.currentProject = action.payload.generalInformation.project;
      state.currentOverview = action.payload.generalInformation.overview;
      state.currentProcessDetails = action.payload.generalInformation.processDetails;
      state.currentQuoteTemplateType = action.payload.generalInformation.quoteTemplateType?.label ?? EMPTY_STRING;
      state.currentRFXLineItemClassId = action.payload.generalInformation.rfxLineItemClassId;
      state.currentQuoteValidity = action.payload.generalInformation.defaultQuoteValidity ?? DEFAULT_QUOTE_VALIDITY;
    }
  },
});

export const {
  setCurrentBusinessUnit,
  setCurrentRFXName,
  setCurrentSiteNameFBN,
  setCurrentBeginDate,
  setCurrentBeginTime,
  setCurrentDueDate,
  setCurrentDueTime,
  setCurrentNeedByDate,
  setCurrentNeedByTime,
  setCurrentRegion,
  setCurrentRegions,
  setCurrentCategory,
  setCurrentSubCategory,
  setCurrentCategories,
  setCurrentCurrency,
  setCurrentOrganization,
  setCurrentDepartment,
  setCurrentProject,
  setCurrentOverview,
  setCurrentProcessDetails,
  setCurrentQuoteTemplateType,
  setCurrentRFXLineItemClassId,
  setCurrentQuoteValidity,
  setCurrentRFXInformation
} = rfxInformationSlice.actions;

export const selectCurrentBusinessUnit= (state: RootState) => {
  return state.rfxInformation.currentBusinessUnit;
}

export const selectCurrentRFXName= (state: RootState) => {
  return state.rfxInformation.currentRFXName;
}

export const selectCurrentSiteNameFBN= (state: RootState) => {
  return state.rfxInformation.currentSiteNameFBN;
}

export const selectCurrentBeginDate= (state: RootState) => {
  return state.rfxInformation.currentBeginDate;
}

export const selectCurrentBeginTime= (state: RootState) => {
  return state.rfxInformation.currentBeginTime;
}

export const selectCurrentDueDate= (state: RootState) => {
  return state.rfxInformation.currentDueDate;
}

export const selectCurrentDueTime= (state: RootState) => {
  return state.rfxInformation.currentDueTime;
}

export const selectCurrentNeedByDate= (state: RootState) => {
  return state.rfxInformation.currentNeedByDate;
}

export const selectCurrentNeedByTime= (state: RootState) => {
  return state.rfxInformation.currentNeedByTime;
}

export const selectCurrentRegion= (state: RootState) => {
  return state.rfxInformation.currentRegion;
}

export const selectCurrentRegions = (state: RootState) => {
  return state.rfxInformation.currentRegions;
}

export const selectCurrentCategory = (state: RootState) => {
  return state.rfxInformation.currentCategory;
}

export const selectCurrentSubCategory = (state: RootState) => {
  return state.rfxInformation.currentSubCategory;
}

export const selectCurrentCategories = (state: RootState) => {
  return state.rfxInformation.currentCategories;
}

export const selectCurrentCurrency = (state: RootState) => {
  return state.rfxInformation.currentCurrency;
}

export const selectCurrentOrganization = (state: RootState) => {
  return state.rfxInformation.currentOrganization;
}

export const selectCurrentDepartment = (state: RootState) => {
  return state.rfxInformation.currentDepartment;
}

export const selectCurrentProject = (state: RootState) => {
  return state.rfxInformation.currentProject;
}

export const selectCurrentOverview = (state: RootState) => {
  return state.rfxInformation.currentOverview;
}

export const selectCurrentProcessDetails = (state: RootState) => {
  return state.rfxInformation.currentProcessDetails;
}

export const selectCurrentQuoteTemplateType = (state: RootState) => {
  return state.rfxInformation.currentQuoteTemplateType;
}

export const selectCurrentRFXLineItemClassId = (state: RootState) => {
  return state.rfxInformation.currentRFXLineItemClassId;
}

export const selectCurrentQuoteValidity = (state: RootState) => {
  return state.rfxInformation.currentQuoteValidity;
}

export default rfxInformationSlice.reducer;
